<script>
  import { createEventDispatcher } from "svelte";
  import { portal } from "../../helpers.js";
  import { activeRouteSchedule } from "../../stores.js";
  import { Close } from "carbon-icons-svelte";

  const dispatch = createEventDispatcher();

  export let scheduleData = null;

  // Extract properties safely
  $: yard = scheduleData?.yard || {};
  $: load = scheduleData?.load || {};
  $: routes = scheduleData?.routes || load?.routes || [];
  $: material = scheduleData?.material || "";
  $: tonnes = scheduleData?.tonnes || 0;
  $: tonneRate = scheduleData?.tonneRate;

  let activeFilter = "all"; // 'all' | 'outbound' | 'inbound'

  function close() {
    activeRouteSchedule.set(null);
    dispatch("close");
  }

  function handleKeydown(e) {
    if (e.key === "Escape") {
      close();
    }
  }

  // Parse duration "HH:MM" into minutes
  function parseMinutes(durationStr) {
    if (!durationStr || typeof durationStr !== "string") return 0;
    const parts = durationStr.split(":");
    if (parts.length < 2) return 0;
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  }

  function formatDurationText(durationStr) {
    if (!durationStr || typeof durationStr !== "string") return "—";
    const mins = parseMinutes(durationStr);
    if (mins === 0) return "0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  function formatCurrency(amount) {
    if (amount == null || isNaN(amount)) return "$0.00";
    return amount.toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Filtered routes
  $: filteredRoutes = routes.filter((r) => {
    if (activeFilter === "outbound") return r.direction === "load to tip";
    if (activeFilter === "inbound") return r.direction === "tip to load";
    return true;
  });

  // Analytics
  $: outboundLegs = routes.filter((r) => r.direction === "load to tip");
  $: inboundLegs = routes.filter((r) => r.direction === "tip to load");
  $: totalLoadsDelivered = outboundLegs.length;
  $: totalLegsCount = routes.length;

  $: totalTollCents = routes.reduce((acc, r) => acc + (r.toll || 0), 0);
  $: totalTollDollars = totalTollCents / 100;

  $: totalMinutes = routes.reduce((acc, r) => acc + parseMinutes(r.duration_total), 0);
  $: totalTravelMinutes = routes.reduce((acc, r) => acc + parseMinutes(r.duration_travel), 0);
  $: totalSiteMinutes = routes.reduce(
    (acc, r) => acc + parseMinutes(r.duration_load) + parseMinutes(r.duration_tip || r.duration_trip),
    0
  );

  $: firstCompleted = routes[0]?.completed_at || "—";
  $: lastCompleted = routes[routes.length - 1]?.completed_at || "—";

  $: yardTitle =
    yard?.company?.name ||
    yard?.name ||
    load?.yard?.company?.name ||
    load?.yard?.name ||
    "Disposal Yard";

  $: yardSuburb = yard?.address?.suburb || load?.yard?.address?.suburb || "";
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div use:portal class="m3-dialog-scrim" on:click|self={close}>
  <div
    class="m3-dialog-surface route-schedule-surface"
    role="dialog"
    aria-modal="true"
    aria-labelledby="dialog-schedule-title"
  >
    <!-- Modal Header -->
    <div class="m3-dialog-header">
      <div class="header-main-info">
        <div class="header-title-row">
          <div class="header-icon-circle">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="#2563eb">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          <div class="header-title-text-group">
            <div class="header-name-row">
              <h2 id="dialog-schedule-title" class="dialog-title">{yardTitle}</h2>
              {#if yardSuburb}
                <span class="m3-suburb-chip">{yardSuburb}</span>
              {/if}
              {#if material}
                <span class="m3-material-chip">{material}</span>
              {/if}
            </div>
            <span class="dialog-subtitle">
              Daily Turnaround Schedule & Driving Route Breakdown
            </span>
          </div>
        </div>
      </div>
      <button class="close-icon-btn" on:click={close} title="Close schedule" type="button">
        <Close size={20} />
      </button>
    </div>

    <!-- Summary KPI Cards -->
    <div class="schedule-kpi-grid">
      <div class="kpi-card highlight-loads">
        <div class="kpi-header">
          <span class="kpi-icon">🚛</span>
          <span class="kpi-label">Delivered Loads</span>
        </div>
        <div class="kpi-val">{totalLoadsDelivered} <span class="kpi-unit">runs</span></div>
        <div class="kpi-sub">
          {totalLegsCount} total turnaround legs
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-icon">⏱️</span>
          <span class="kpi-label">Shift Window</span>
        </div>
        <div class="kpi-val">{firstCompleted} – {lastCompleted}</div>
        <div class="kpi-sub">
          Total: {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m duration
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-icon">🛣️</span>
          <span class="kpi-label">Total Tolls</span>
        </div>
        <div class="kpi-val">{formatCurrency(totalTollDollars)}</div>
        <div class="kpi-sub">
          {routes.filter((r) => r.toll > 0).length} tolled legs
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-icon">⏳</span>
          <span class="kpi-label">Driving vs Site Time</span>
        </div>
        <div class="kpi-val">
          {Math.floor(totalTravelMinutes / 60)}h {totalTravelMinutes % 60}m
          <span class="kpi-unit">travel</span>
        </div>
        <div class="kpi-sub">
          Site handling: {Math.floor(totalSiteMinutes / 60)}h {totalSiteMinutes % 60}m
        </div>
      </div>
    </div>

    <!-- Filter Pills Bar -->
    <div class="schedule-filter-bar">
      <div class="filter-pills">
        <button
          type="button"
          class="filter-pill {activeFilter === 'all' ? 'active' : ''}"
          on:click={() => (activeFilter = "all")}
        >
          All Turnarounds ({routes.length})
        </button>
        <button
          type="button"
          class="filter-pill {activeFilter === 'outbound' ? 'active' : ''}"
          on:click={() => (activeFilter = "outbound")}
        >
          Outbound Loads ({outboundLegs.length})
        </button>
        <button
          type="button"
          class="filter-pill {activeFilter === 'inbound' ? 'active' : ''}"
          on:click={() => (activeFilter = "inbound")}
        >
          Return Trips ({inboundLegs.length})
        </button>
      </div>

      <div class="schedule-summary-hint">
        Showing <strong>{filteredRoutes.length}</strong> of {routes.length} turnaround legs
      </div>
    </div>

    <!-- Turnaround Schedule Table -->
    <div class="schedule-table-wrap">
      {#if filteredRoutes.length === 0}
        <div class="empty-schedule">
          <span class="empty-icon">📭</span>
          <p>No route turnaround legs match the selected filter.</p>
        </div>
      {:else}
        <table class="schedule-table">
          <thead>
            <tr>
              <th class="th-leg">#</th>
              <th class="th-dir">Direction</th>
              <th class="th-completed">Completed</th>
              <th class="th-dur">Load Time</th>
              <th class="th-dur">Travel Time</th>
              <th class="th-dur">Tip Time</th>
              <th class="th-dur">Total Leg</th>
              <th class="th-toll">Toll</th>
            </tr>
          </thead>
          <tbody>
            {#each filteredRoutes as leg, idx (idx)}
              <tr class="leg-row {leg.direction === 'load to tip' ? 'is-outbound' : 'is-inbound'}">
                <td class="td-leg">
                  <span class="leg-num">{idx + 1}</span>
                </td>
                <td class="td-dir">
                  {#if leg.direction === "load to tip"}
                    <span class="direction-badge outbound">
                      <span class="dir-icon">🚛</span>
                      <span class="dir-text">Load site ➔ Disposal Yard</span>
                    </span>
                  {:else}
                    <span class="direction-badge inbound">
                      <span class="dir-icon">🔄</span>
                      <span class="dir-text">Disposal Yard ➔ Load site</span>
                    </span>
                  {/if}
                </td>
                <td class="td-completed">
                  <span class="completed-time-pill">{leg.completed_at || "—"}</span>
                </td>
                <td class="td-dur">
                  {#if leg.duration_load}
                    <span class="dur-badge dur-load">{formatDurationText(leg.duration_load)}</span>
                  {:else}
                    <span class="dur-empty">—</span>
                  {/if}
                </td>
                <td class="td-dur">
                  <span class="dur-badge dur-travel">{formatDurationText(leg.duration_travel)}</span>
                </td>
                <td class="td-dur">
                  {#if leg.duration_tip || leg.duration_trip}
                    <span class="dur-badge dur-tip">{formatDurationText(leg.duration_tip || leg.duration_trip)}</span>
                  {:else}
                    <span class="dur-empty">—</span>
                  {/if}
                </td>
                <td class="td-dur td-total-dur">
                  <strong class="dur-total-text">{formatDurationText(leg.duration_total)}</strong>
                </td>
                <td class="td-toll">
                  {#if leg.toll_error}
                    <span class="toll-badge error" title={leg.toll_error}>ERROR</span>
                  {:else if leg.toll && leg.toll > 0}
                    <span class="toll-badge has-toll">{formatCurrency(leg.toll / 100)}</span>
                  {:else}
                    <span class="toll-badge free">No toll</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
          <tfoot>
            <tr class="tfoot-row">
              <td colspan="2" class="tf-label">Total Shift Breakdown</td>
              <td class="tf-completed">{lastCompleted}</td>
              <td class="tf-val">{formatDurationText(routes.reduce((acc, r) => acc + parseMinutes(r.duration_load), 0) + ":00")}</td>
              <td class="tf-val">{formatDurationText(totalTravelMinutes + ":00")}</td>
              <td class="tf-val">{formatDurationText(routes.reduce((acc, r) => acc + parseMinutes(r.duration_tip || r.duration_trip), 0) + ":00")}</td>
              <td class="tf-val tf-total">{formatDurationText(totalMinutes + ":00")}</td>
              <td class="tf-toll">{formatCurrency(totalTollDollars)}</td>
            </tr>
          </tfoot>
        </table>
      {/if}
    </div>

    <!-- Modal Footer -->
    <div class="m3-dialog-footer">
      <div class="footer-left-hint">
        <span>Tolls and turnaround schedules computed by route navigation engine.</span>
      </div>
      <button class="m3-btn m3-btn-primary" on:click={close} type="button">
        Done
      </button>
    </div>
  </div>
</div>

<style>
  /* Material Design 3 Dialog Scrim */
  .m3-dialog-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.52);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    animation: m3FadeIn 0.2s cubic-bezier(0.2, 0, 0, 1);
  }

  @keyframes m3FadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Material Design 3 Dialog Surface Container */
  .m3-dialog-surface {
    background-color: #ffffff;
    border-radius: 20px;
    box-shadow:
      0 24px 48px -12px rgba(15, 23, 42, 0.25),
      0 12px 24px -6px rgba(15, 23, 42, 0.12),
      0 0 0 1px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: m3ScaleIn 0.22s cubic-bezier(0.2, 0, 0, 1);
  }

  @keyframes m3ScaleIn {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .route-schedule-surface {
    max-width: 900px;
    width: 95vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Header / Top Bar */
  .m3-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    background-color: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
  }

  .close-icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  .close-icon-btn:hover {
    background-color: #f1f5f9;
    color: #0f172a;
  }

  /* Footer */
  .m3-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    background-color: #ffffff;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
  }

  .m3-btn {
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 9999px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0 24px;
    border: none;
  }

  .m3-btn-primary {
    background-color: #2563eb;
    color: #ffffff;
    box-shadow: 0 1px 4px rgba(37, 99, 235, 0.35);
  }

  .m3-btn-primary:hover {
    background-color: #1d4ed8;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.45);
    transform: translateY(-1px);
  }

  .header-icon-circle {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-title-text-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .header-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .m3-suburb-chip {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    padding: 2px 7px;
    border-radius: 4px;
  }

  .m3-material-chip {
    font-size: 11px;
    font-weight: 600;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    padding: 2px 7px;
    border-radius: 4px;
  }

  /* KPI Grid */
  .schedule-kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 14px 20px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  @media (max-width: 768px) {
    .schedule-kpi-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .kpi-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .kpi-card.highlight-loads {
    border-color: #93c5fd;
    background: #f0f7ff;
  }

  .kpi-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #64748b;
  }

  .kpi-val {
    font-size: 17px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    margin-top: 2px;
  }

  .kpi-unit {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
  }

  .kpi-sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  /* Filter Bar */
  .schedule-filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    flex-wrap: wrap;
    gap: 10px;
  }

  .filter-pills {
    display: flex;
    gap: 6px;
  }

  .filter-pill {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .filter-pill:hover {
    background: #e2e8f0;
    color: #1e293b;
  }

  .filter-pill.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
    box-shadow: 0 1px 4px rgba(37, 99, 235, 0.25);
  }

  .schedule-summary-hint {
    font-size: 12px;
    color: #64748b;
  }

  /* Table Wrap */
  .schedule-table-wrap {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    min-height: 220px;
  }

  .schedule-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    text-align: left;
  }

  .schedule-table thead th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    color: #475569;
    font-weight: 700;
    padding: 10px 14px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
    z-index: 2;
  }

  .schedule-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background-color 0.12s ease;
  }

  .schedule-table tbody tr:hover {
    background-color: #f8fafc;
  }

  .schedule-table td {
    padding: 9px 14px;
    vertical-align: middle;
  }

  .th-leg, .td-leg {
    width: 40px;
    text-align: center;
  }

  .leg-num {
    display: inline-block;
    width: 22px;
    height: 22px;
    line-height: 22px;
    text-align: center;
    background: #e2e8f0;
    color: #334155;
    font-weight: 700;
    border-radius: 50%;
    font-size: 11px;
  }

  .direction-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 5px;
    font-weight: 600;
    font-size: 11.5px;
  }

  .direction-badge.outbound {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .direction-badge.inbound {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
  }

  .completed-time-pill {
    font-weight: 700;
    color: #0f172a;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 11.5px;
  }

  .dur-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 11.5px;
  }

  .dur-load {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }

  .dur-travel {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #dbeafe;
  }

  .dur-tip {
    background: #f3e8ff;
    color: #6b21a8;
    border: 1px solid #e9d5ff;
  }

  .dur-total-text {
    font-size: 12px;
    color: #0f172a;
  }

  .dur-empty {
    color: #94a3b8;
  }

  .toll-badge {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 4px;
    font-weight: 700;
    font-size: 11.5px;
  }

  .toll-badge.has-toll {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .toll-badge.free {
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
  }

  .toll-badge.error {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fca5a5;
    cursor: help;
    font-weight: 800;
    letter-spacing: 0.03em;
  }

  .toll-badge.error:hover {
    background: #fecaca;
  }

  /* Footer Total Row */
  .tfoot-row td {
    background: #f8fafc;
    font-weight: 700;
    padding: 10px 14px;
    border-top: 2px solid #cbd5e1;
    color: #1e293b;
  }

  .tf-total {
    font-size: 13px;
    color: #2563eb;
  }

  .tf-toll {
    font-size: 13px;
    color: #b91c1c;
  }

  /* Empty state */
  .empty-schedule {
    text-align: center;
    padding: 40px 20px;
    color: #64748b;
  }

  .empty-icon {
    font-size: 32px;
    display: block;
    margin-bottom: 8px;
  }

  /* Footer */
  .footer-left-hint {
    font-size: 11.5px;
    color: #64748b;
  }
</style>
